import {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useRef,
    ReactNode,
} from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { HelpCircle, X, ChevronRight } from 'lucide-react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'

export interface QuickHelpOption {
    id: string
    label: string
    description: string
    /** CSS selector (data-tour) OR button label text */
    selector?: string
    buttonLabel?: string
    tourTitle: string
    tourDescription: string
    /** Optional: Navigate to this path before showing hint */
    navigateTo?: string
    /** Optional: Demo animation type from tour-styles.ts (for single hint) */
    demo?:
        | 'sidebar-menu-expand-dayoff'
        | 'sidebar-menu-expand-business'
        | 'sidebar-submenu-select-dayoff'
        | 'sidebar-submenu-select-business'
        | 'click-create-button'
        | 'sidebar-click-customer'
        | 'customer-create-options'
        | 'drag'
        | 'resize'
        | 'hide'
        | 'contribute'
        | 'delete-modal'
        | 'pay-modal'
        | 'send-mail'
        | 'add-update'
        | 'add-invoice-line'
        | 'preview-mail'
        | 'select-dropdown'
        | 'customer-select'
        | 'service-select'
        | 'priority-select'
        | 'assignee-select'
        | 'textarea-input'
        | 'datetime-picker'
        | 'credit-transaction-modal'
        | 'lead-detail-modal'
        | 'lead-status-dropdown'
        | 'lead-filters-overview'
        | 'classify-lead-modal'
        | 'add-contact-modal'
        | 'add-note-modal'
        | 'dayoff-edit-action'
        | 'dayoff-cancel-action'
        | 'select-business-rows'
        | 'click-bulk-approve-business-button'
        | 'bulk-approve-business-modal'
        | 'business-cancel-action'
    /** Optional: Multi-step tour steps (uses tour-styles.ts) */
    tourSteps?: Array<{
        selector: string
        title: string
        content: string
        position?: 'top' | 'right' | 'bottom' | 'left'
        /** Optional: Demo animation type from tour-styles.ts */
        demo?:
            | 'sidebar-menu-expand-dayoff'
            | 'sidebar-menu-expand-business'
            | 'sidebar-submenu-select-dayoff'
            | 'sidebar-submenu-select-business'
            | 'click-create-button'
            | 'sidebar-click-customer'
            | 'customer-create-options'
            | 'drag'
            | 'resize'
            | 'hide'
            | 'contribute'
            | 'delete-modal'
            | 'pay-modal'
            | 'send-mail'
            | 'add-update'
            | 'add-invoice-line'
            | 'preview-mail'
            | 'select-dropdown'
            | 'customer-select'
            | 'service-select'
            | 'priority-select'
            | 'assignee-select'
            | 'textarea-input'
            | 'datetime-picker'
            | 'credit-transaction-modal'
            | 'lead-detail-modal'
            | 'lead-status-dropdown'
            | 'lead-filters-overview'
            | 'classify-lead-modal'
            | 'add-contact-modal'
            | 'add-note-modal'
            | 'dayoff-edit-action'
            | 'dayoff-cancel-action'
            | 'select-dayoff-rows'
            | 'click-bulk-approve-button'
            | 'bulk-approve-dayoff-modal'
            | 'select-business-rows'
            | 'click-bulk-approve-business-button'
            | 'bulk-approve-business-modal'
            | 'business-cancel-action'
    }>
}

interface QuickHelpContextType {
    /** Register quick help options for current page */
    registerOptions: (options: QuickHelpOption[]) => void
    /** Unregister options */
    unregisterOptions: () => void
    /** Check if options exist */
    hasOptions: boolean
}

const QuickHelpContext = createContext<QuickHelpContextType | null>(null)

/**
 * Find element by selector or button label
 */
const findElement = (option: QuickHelpOption): HTMLElement | null => {
    // Try selector first
    if (option.selector) {
        const el = document.querySelector(option.selector) as HTMLElement
        if (el) return el
    }

    // Try button label
    if (option.buttonLabel) {
        const buttons = document.querySelectorAll('button')
        for (const button of buttons) {
            if (button.textContent?.trim() === option.buttonLabel) {
                return button as HTMLElement
            }
        }
    }

    return null
}

export const QuickHelpProvider = ({ children }: { children: ReactNode }) => {
    const navigate = useNavigate()
    const location = useLocation()
    // Stack-based options to support nested components (Modal inside Page)
    const [optionsStack, setOptionsStack] = useState<QuickHelpOption[][]>([])
    const [isOpen, setIsOpen] = useState(false)
    const [isHinting, setIsHinting] = useState(false)
    const optionsRef = useRef<QuickHelpOption[]>([])
    const pendingHintRef = useRef<QuickHelpOption | null>(null)

    // Get current options from top of stack
    const options = optionsStack[optionsStack.length - 1] || []

    // Keep ref in sync
    useEffect(() => {
        optionsRef.current = options
    }, [options])

    const registerOptions = useCallback((newOptions: QuickHelpOption[]) => {
        // Push new options onto stack
        setOptionsStack((prev) => [...prev, newOptions])
    }, [])

    const unregisterOptions = useCallback(() => {
        // Pop options from stack
        setOptionsStack((prev) => prev.slice(0, -1))
        setIsOpen(false)
    }, [])

    /**
     * Show single-step hint using tour-styles.ts (rich styling with keyboard hints)
     */
    const showHintPopover = useCallback((option: QuickHelpOption) => {
        const element = findElement(option)

        setIsHinting(true)
        injectTourStyles()
        cleanupTourElements()
        createTourOverlay()

        // If element found, highlight and scroll to it
        if (element) {
            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }

        setTimeout(
            () => {
                const popover = createTourPopover({
                    title: option.tourTitle,
                    description: option.tourDescription,
                    currentStep: 1,
                    totalSteps: 1,
                    onNext: () => {
                        cleanupTourElements()
                        setIsHinting(false)
                    },
                    onClose: () => {
                        cleanupTourElements()
                        setIsHinting(false)
                    },
                    isLastStep: true,
                    demo: option.demo,
                })

                document.body.appendChild(popover)

                // Position popover: center of screen if no element, or near element
                if (element) {
                    positionPopover(popover, element)
                } else {
                    // Center popover on screen for info-only hints
                    popover.style.position = 'fixed'
                    popover.style.top = '50%'
                    popover.style.left = '50%'
                    popover.style.transform = 'translate(-50%, -50%)'
                }
            },
            element ? 300 : 100
        )
    }, [])

    /**
     * Show multi-step tour using tour-styles.ts
     */
    const showMultiStepTour = useCallback(
        (steps: QuickHelpOption['tourSteps']) => {
            if (!steps || steps.length === 0) return

            let currentStepIndex = 0

            const showStep = (index: number) => {
                const step = steps[index]
                if (!step) return

                cleanupTourElements()
                injectTourStyles()
                createTourOverlay()

                const element = document.querySelector(
                    step.selector
                ) as HTMLElement
                if (element) {
                    element.classList.add('tour-highlight-element')
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    })
                }

                setTimeout(
                    () => {
                        const popover = createTourPopover({
                            title: step.title,
                            description: step.content,
                            currentStep: index + 1,
                            totalSteps: steps.length,
                            onPrev:
                                index > 0
                                    ? () => {
                                          currentStepIndex = index - 1
                                          showStep(currentStepIndex)
                                      }
                                    : undefined,
                            onNext: () => {
                                if (index < steps.length - 1) {
                                    currentStepIndex = index + 1
                                    showStep(currentStepIndex)
                                } else {
                                    cleanupTourElements()
                                    setIsHinting(false)
                                }
                            },
                            onClose: () => {
                                cleanupTourElements()
                                setIsHinting(false)
                            },
                            isLastStep: index === steps.length - 1,
                            demo: step.demo,
                        })

                        document.body.appendChild(popover)

                        if (element) {
                            positionPopover(popover, element)
                        } else {
                            popover.style.position = 'fixed'
                            popover.style.top = '50%'
                            popover.style.left = '50%'
                            popover.style.transform = 'translate(-50%, -50%)'
                        }
                    },
                    element ? 300 : 100
                )
            }

            setIsHinting(true)
            showStep(0)
        },
        []
    )

    const handleSelectOption = useCallback(
        (option: QuickHelpOption) => {
            setIsOpen(false)

            // If tourSteps is provided, start multi-step tour with tour-styles.ts
            if (option.tourSteps && option.tourSteps.length > 0) {
                // Small delay to let menu close animation complete
                setTimeout(() => {
                    showMultiStepTour(option.tourSteps)
                }, 100)
                return
            }

            // If navigateTo is set and we're not already there, navigate first
            if (
                option.navigateTo &&
                !location.pathname.startsWith(option.navigateTo)
            ) {
                pendingHintRef.current = option
                navigate(option.navigateTo)
                return
            }

            // Show single-step hint using tour-styles.ts
            showHintPopover(option)
        },
        [location.pathname, navigate, showHintPopover, showMultiStepTour]
    )

    // Show pending hint after navigation
    useEffect(() => {
        if (
            pendingHintRef.current &&
            location.pathname.startsWith(
                pendingHintRef.current.navigateTo || ''
            )
        ) {
            // Wait for page to render
            const timer = setTimeout(() => {
                if (pendingHintRef.current) {
                    showHintPopover(pendingHintRef.current)
                    pendingHintRef.current = null
                }
            }, 500)
            return () => clearTimeout(timer)
        }
    }, [location.pathname, showHintPopover])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return (
        <QuickHelpContext.Provider
            value={{
                registerOptions,
                unregisterOptions,
                hasOptions: options.length > 0,
            }}
        >
            {children}

            {/* Quick Help Menu UI */}
            {options.length > 0 && (
                <>
                    {/* Floating Button - z-[10000] to be above Drawer (z-[9999]) */}
                    <div className="fixed bottom-6 right-2 z-[10000]">
                        <div className="relative">
                            {/* Clockwise spinning border */}
                            {!isOpen && (
                                <div
                                    className="absolute inset-[-2px] rounded-full animate-spin"
                                    style={{
                                        background:
                                            'conic-gradient(from 0deg, transparent 0deg 270deg, #C8102E 270deg 360deg)',
                                        animationDuration: '2s',
                                    }}
                                />
                            )}
                            {/* Inner background to mask the gradient */}
                            <div className="absolute inset-0 rounded-full bg-white" />

                            <button
                                onClick={() => setIsOpen(!isOpen)}
                                className={`
                                    relative w-8 h-8 rounded-full shadow-lg
                                    flex items-center justify-center
                                    transition-all duration-500
                                    ${
                                        isOpen
                                            ? 'bg-gray-700 text-white rotate-90'
                                            : 'bg-white text-[#C8102E] border border-[#C8102E]/30 hover:border-[#C8102E] hover:shadow-xl'
                                    }
                                `}
                                title="Bạn muốn làm gì?"
                            >
                                {isOpen ? (
                                    <X className="w-3.5 h-3.5" />
                                ) : (
                                    <HelpCircle className="w-4 h-4" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Dropdown Menu - z-[10000] to be above Drawer (z-[9999]) */}
                    {isOpen && !isHinting && (
                        <div className="fixed bottom-[64px] right-2 z-[10000] animate-in slide-in-from-bottom-4 fade-in duration-200">
                            <div className="bg-white rounded-xl shadow-2xl border border-gray-100 w-80 max-h-[60vh] overflow-hidden">
                                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                                    <h3 className="font-semibold text-gray-800">
                                        Bạn muốn làm gì?
                                    </h3>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        Chọn action để xem hướng dẫn
                                    </p>
                                </div>

                                <div className="overflow-y-auto max-h-[calc(60vh-60px)]">
                                    {options.map((option) => (
                                        <button
                                            key={option.id}
                                            onClick={() =>
                                                handleSelectOption(option)
                                            }
                                            className="w-full px-4 py-3 text-left hover:bg-bg-primary/5 transition-colors border-b border-gray-50 last:border-b-0 group"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-800 group-hover:text-bg-primary">
                                                        {option.label}
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-0.5">
                                                        {option.description}
                                                    </div>
                                                </div>
                                                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-bg-primary group-hover:translate-x-1 transition-all" />
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </QuickHelpContext.Provider>
    )
}

/**
 * Hook to register quick help options for current page
 */
export const useQuickHelp = (options: QuickHelpOption[]) => {
    const context = useContext(QuickHelpContext)
    if (!context) {
        throw new Error('useQuickHelp must be used within QuickHelpProvider')
    }

    const { registerOptions, unregisterOptions } = context

    // Track if we actually registered to avoid unbalanced push/pop
    const didRegisterRef = useRef(false)

    useEffect(() => {
        if (options.length > 0) {
            registerOptions(options)
            didRegisterRef.current = true
        } else {
            didRegisterRef.current = false
        }
        return () => {
            // Only unregister if we actually registered
            if (didRegisterRef.current) {
                unregisterOptions()
                didRegisterRef.current = false
            }
        }
    }, [options, registerOptions, unregisterOptions])
}

/**
 * Hook to access quick help context directly
 */
export const useQuickHelpContext = () => {
    const context = useContext(QuickHelpContext)
    if (!context) {
        throw new Error(
            'useQuickHelpContext must be used within QuickHelpProvider'
        )
    }
    return context
}

export default QuickHelpProvider
